<template>
  <v-dialog v-model="internalValue" max-width="1300px" scrollable>
    <v-card v-if="budget" class="rounded-xl border-light bg-white">
      <!-- HEADER -->
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom bg-white flex-wrap">
        <div class="d-flex align-center">
          <v-avatar color="blue lighten-5" size="36" class="mr-3">
            <v-icon color="primary" small>mdi-file-document-outline</v-icon>
          </v-avatar>
          <div>
            <div class="d-flex align-center flex-wrap">
              <span class="text-h6 font-weight-black grey--text text--darken-4 mr-2">
                {{ budget.EmbName }}
              </span>
              <v-chip small label outlined color="primary" class="font-weight-bold mr-2">
                v{{ budget.EmbVersion }}
              </v-chip>
              <v-chip small :color="statusColor" class="font-weight-bold mr-1" text-color="white">
                {{ budget.StatusName || 'Borrador' }}
              </v-chip>
              <v-chip
                v-if="budget.EmbStatus === 3 || budget.EmbIsCurrent === false || budget.EmbIsCurrent === 0"
                small
                label
                outlined
                color="error"
                class="font-weight-bold"
              >
                No vigente
              </v-chip>
            </div>
            <div class="caption grey--text text--darken-2">
              {{ budget.CmpName || 'Campaña' }} • {{ budget.TypeDestinationName || 'División' }} • Base: {{ formatDate(budget.EmbDate) }} • Moneda: {{ budget.TypeCurrencyName || 'USD' }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <!-- TABS DE NAVEGACIÓN -->
          <v-tabs v-model="activeTab" class="detail-tabs mr-3" height="36" color="primary">
            <v-tab class="text-none font-weight-bold">
              <v-icon small class="mr-1">mdi-chart-timeline-variant</v-icon>
              Seguimiento de Programación
            </v-tab>
            <v-tab class="text-none font-weight-bold">
              <v-icon small class="mr-1">mdi-table</v-icon>
              Metas Presupuestadas
            </v-tab>
          </v-tabs>

          <v-btn icon color="grey darken-2" small @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- BODY -->
      <v-card-text class="pt-4 pb-4 px-5 bg-white" style="max-height: 82vh;">
        <!-- ALERTA DE PRESUPUESTO NO VIGENTE / ANULADO -->
        <v-alert
          v-if="budget.EmbStatus === 3 || budget.EmbIsCurrent === false || budget.EmbIsCurrent === 0"
          dense
          outlined
          type="warning"
          class="mb-3 rounded-lg text-caption bg-white"
        >
          <strong>Consulta Histórica (No Vigente):</strong> Este presupuesto maestro se encuentra {{ budget.EmbStatus === 3 ? 'anulado' : 'como versión histórica no vigente' }}. Su visualización es exclusivamente de lectura histórica y no permite nuevas programaciones ni modificaciones operativas.
        </v-alert>

        <!-- PESTAÑA 1: SEGUIMIENTO DE PROGRAMACIÓN VS PRESUPUESTO -->
        <div v-show="activeTab === 0">
          <BudgetProgrammingTracking
            v-if="budget && budget.EmbID"
            :emb-id="budget.EmbID"
            :budget-data="budget"
          />
        </div>

        <!-- PESTAÑA 2: METAS PRESUPUESTADAS ORIGINALES -->
        <div v-show="activeTab === 1">
          <!-- INFO GENERAL -->
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-3">
            <v-icon small color="grey darken-3" class="mr-1">mdi-clipboard-text-outline</v-icon>
            Información del Presupuesto Base
          </div>

          <v-card outlined class="pa-3 mb-4 rounded-xl border-light bg-grey-lighten-5" elevation="0">
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Campaña</span>
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">{{ budget.CmpName || '-' }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">División / Destino</span>
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">{{ budget.TypeDestinationName || '-' }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Fecha Base</span>
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">{{ formatDate(budget.EmbDate) }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Moneda</span>
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">{{ budget.TypeCurrencyName || 'USD' }}</div>
              </v-col>
              <v-col cols="12" v-if="budget.EmbObservation" class="mt-2">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Observaciones</span>
                <div class="body-2 grey--text text--darken-4">{{ budget.EmbObservation }}</div>
              </v-col>
            </v-row>
          </v-card>

          <!-- KPI SUMMARY CARDS -->
          <v-card outlined class="pa-3 mb-4 rounded-xl border-light bg-grey-lighten-5" elevation="0">
            <v-row dense align="center">
              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Clientes</span>
                <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                  {{ budget.TotalClients }}
                </div>
              </v-col>
              <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Toneladas</span>
                <div class="text-subtitle-1 font-weight-black green--text text--darken-3">
                  {{ Number(budget.TotalTons || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }} TM
                </div>
              </v-col>
              <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Contenedores</span>
                <div class="text-subtitle-1 font-weight-black teal--text text--darken-3">
                  {{ Number(budget.TotalFcl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }} FCL
                </div>
              </v-col>
              <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Valor FOB</span>
                <div class="text-subtitle-1 font-weight-black blue--text text--darken-3">
                  ${{ Number(budget.TotalFobUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
                </div>
              </v-col>
              <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Prom. TM/FCL</span>
                <div class="text-subtitle-1 font-weight-black orange--text text--darken-3">
                  {{ budget.TotalFcl > 0 ? (budget.TotalTons / budget.TotalFcl).toFixed(2) + ' TM' : '-' }}
                </div>
              </v-col>
              <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

              <v-col cols="6" md="2" class="text-center">
                <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">FOB Promedio USD/kg</span>
                <div class="text-subtitle-1 font-weight-black purple--text text--darken-3">
                  {{ (budget.TotalTons > 0 && budget.TotalFobUsd > 0) ? '$' + (budget.TotalFobUsd / (budget.TotalTons * 1000)).toFixed(4) + ' /kg' : '-' }}
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- AUDITORÍA DE BLOQUEO -->
          <v-alert v-if="budget.EmbStatus === 2" dense outlined type="success" class="mb-3 rounded-lg">
            <strong>Presupuesto Aprobado y Bloqueado:</strong> Aprobado por <code>{{ budget.LockedUserName || 'Usuario Autorizado' }}</code> el {{ formatDateTime(budget.EmbLockedDate) }}.
          </v-alert>

          <!-- TABLA DE LÍNEAS ORIGINALES -->
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-2">
            <v-icon small color="grey darken-3" class="mr-1">mdi-account-group-outline</v-icon>
            Desglose de Metas Presupuestadas por Cliente:
          </div>
          <v-data-table
            :headers="budgetHeaders"
            :items="budget.Details || []"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="border-rounded border-light elevation-0 budget-table"
          >
            <template v-slot:[`item.index`]="{ index }">
              <span class="caption font-weight-bold grey--text text--darken-2">{{ index + 1 }}</span>
            </template>

            <template v-slot:[`item.Type`]="{ item }">
              <v-chip x-small label :color="item.EmdIsNoClient ? 'amber darken-3' : 'primary'" text-color="white" class="font-weight-bold">
                {{ item.EmdIsNoClient ? 'Sin Cliente' : 'Cliente' }}
              </v-chip>
            </template>

            <template v-slot:[`item.CustomerName`]="{ item }">
              <strong v-if="item.PrsCustomerID">{{ item.PrsCustomerID }} - </strong>
              <span>{{ item.EmdCustomerName }}</span>
            </template>

            <template v-slot:[`item.EmdBudgetTons`]="{ item }">
              <span class="font-weight-bold green--text text--darken-3">{{ Number(item.EmdBudgetTons).toFixed(2) }}</span>
            </template>

            <template v-slot:[`item.EmdBudgetFcl`]="{ item }">
              <span class="font-weight-bold teal--text text--darken-3">{{ Number(item.EmdBudgetFcl).toFixed(2) }}</span>
            </template>

            <template v-slot:[`item.EmdPriceFobKg`]="{ item }">
              <span class="font-weight-bold purple--text text--darken-3">${{ Number(item.EmdPriceFobKg !== undefined && item.EmdPriceFobKg !== null ? item.EmdPriceFobKg : (item.EmdBudgetTons > 0 ? item.EmdBudgetFobUsd / (item.EmdBudgetTons * 1000) : 0)).toFixed(4) }}</span>
            </template>

            <template v-slot:[`item.EmdBudgetFobUsd`]="{ item }">
              <span class="font-weight-bold blue--text text--darken-3">${{ Number(item.EmdBudgetFobUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
            </template>

            <template v-slot:[`item.AverageTmFcl`]="{ item }">
              <span class="font-weight-bold grey--text text--darken-2">
                {{ (item.EmdBudgetFcl > 0 && item.EmdBudgetTons > 0) ? (item.EmdBudgetTons / item.EmdBudgetFcl).toFixed(2) : '-' }}
              </span>
            </template>

            <template v-slot:[`item.EmdObservation`]="{ item }">
              <small class="grey--text text--darken-2">{{ item.EmdObservation || '-' }}</small>
            </template>
          </v-data-table>
        </div>
      </v-card-text>

      <!-- Modal Footer -->
      <v-card-actions class="px-5 pb-4 pt-3 border-light-top bg-white d-flex justify-space-between align-center">
        <span class="caption grey--text text--darken-2">
          Presupuesto Maestro Comercial #{{ budget.EmbID }}
        </span>
        <v-btn text class="rounded-lg px-4 text-none font-weight-bold" @click="close">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CommercialMasterBudgetService from "@/services/ExternalTrade/CommercialMasterBudgetService";
import BudgetProgrammingTracking from "./components/BudgetProgrammingTracking.vue";

export default {
  name: "CommercialMasterBudgetDetailModal",
  components: {
    BudgetProgrammingTracking,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    budgetId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      budget: null,
      activeTab: 1,
      budgetHeaders: [
        { text: "#", value: "index", align: "center", width: "45px" },
        { text: "Tipo", value: "Type", align: "center", width: "110px" },
        { text: "Cliente Comercial (SAP)", value: "CustomerName", align: "left" },
        { text: "TM", value: "EmdBudgetTons", align: "right" },
        { text: "FCL", value: "EmdBudgetFcl", align: "right" },
        { text: "FOB USD/kg", value: "EmdPriceFobKg", align: "right" },
        { text: "FOB Total USD", value: "EmdBudgetFobUsd", align: "right" },
        { text: "TM/FCL", value: "AverageTmFcl", align: "center" },
        { text: "Observaciones", value: "EmdObservation", align: "left" },
      ],
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    statusColor() {
      if (!this.budget) return "info";
      if (this.budget.EmbStatus === 2) return "success";
      if (this.budget.EmbStatus === 3) return "error";
      return "info";
    },
  },
  created() {
    if (this.budgetId && this.budgetId > 0) {
      this.loadBudget(this.budgetId);
    }
  },
  watch: {
    value(val) {
      if (val && this.budgetId && this.budgetId > 0) {
        this.loadBudget(this.budgetId);
      }
    },
    budgetId(newId) {
      if (this.value && newId && newId > 0) {
        this.loadBudget(newId);
      }
    },
  },
  methods: {
    loadBudget(id) {
      CommercialMasterBudgetService.getById(id, this.$fun.getUserID())
        .then((res) => {
          this.budget = res.data;
          // Si el presupuesto está bloqueado o activo, mostrar preferentemente la pestaña de seguimiento
          if (this.budget && this.budget.EmbStatus === 2) {
            this.activeTab = 0;
          }
        })
        .catch((err) => {
          this.$fun.alert(
            "Error al obtener detalle del presupuesto: " +
              (err.response?.data?.Message || err.message),
            "error"
          );
        });
    },
    close() {
      this.internalValue = false;
    },
    formatDate(d) {
      if (!d) return "-";
      return d.substr(0, 10);
    },
    formatDateTime(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString();
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #e2e8f0 !important;
}

.budget-table ::v-deep th {
  background-color: #f8fafc !important;
  color: #1e293b !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0 !important;
}

.budget-table ::v-deep td {
  font-size: 0.825rem !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
